import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Utensils,
  HandHeart,
  HeartPulse,
  Sparkles,
  Trophy,
  Handshake,
  Phone as PhoneIcon,
  Clock,
  Repeat,
  BookOpen,
  MessageCircle,
  Users,
  Building2,
  Calendar,
  MapPin,
  ShieldCheck,
  Leaf,
  Mail,
  Briefcase,
  Coins,
  LineChart,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import LaunchCountdown from "@/components/LaunchCountdown";
import USBasesMap from "@/components/USBasesMap";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const IMG = {
  hero: "https://images.unsplash.com/photo-1526666361175-e3595627c376?auto=format&fit=crop&w=1920&q=80",
  prayer: "https://images.pexels.com/photos/36196051/pexels-photo-36196051.jpeg?auto=compress&cs=tinysrgb&w=1600",
  columbus: "https://images.pexels.com/photos/31193155/pexels-photo-31193155.jpeg?auto=compress&cs=tinysrgb&w=1600",
  serving: "https://images.unsplash.com/photo-1593113616828-6f22bca04804?auto=format&fit=crop&w=1600&q=80",
  badge: "https://images.unsplash.com/photo-1734209648349-f316c5d5c2e9?auto=format&fit=crop&w=1200&q=80",
  family: "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1200&q=80",
  // Branded Feeding Blue photography
  behind_the_badge: "/assets/behind_the_badge.jpg",
  basket_handoff: "/assets/basket_handoff.jpg",
  bring_hope_meals_presence: "/assets/bring_hope_meals_presence.jpg",
  volunteers_packing: "/assets/volunteers_packing.jpg",
  launch_banner: "/assets/launch_banner.jpg",
  // Fallback ambient
  flag_silhouettes: "https://images.pexels.com/photos/1046399/pexels-photo-1046399.jpeg?auto=compress&cs=tinysrgb&w=1600",
  neighborly_help: "https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg?auto=compress&cs=tinysrgb&w=1400",
  warm_dinner: "https://images.pexels.com/photos/3184183/pexels-photo-3184183.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

const useReveal = () => {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll(".fb-reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

export default function Home() {
  useReveal();

  return (
    <div className="bg-white">
      <Nav />

      {/* HERO */}
      <section
        id="home"
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-blue-400 bg-blue-500/10 backdrop-blur-sm px-4 py-2 mb-6 fb-reveal" data-testid="hero-launch-pill">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">
                Officially Launching January 2027
              </span>
            </div>
            <div className="eyebrow text-blue-400 mb-6 fb-reveal">Family • Community • Front Line</div>
            <h1 className="font-display text-white text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.9] tracking-tighter fb-reveal">
              Standing With<br />
              Those Who<br />
              <span className="text-blue-500">Serve.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-200 leading-relaxed fb-reveal">
              Feeding Blue is a nonprofit headquartered in Columbus, Ohio. We feed, uplift, and
              support law enforcement officers and their families during their most vulnerable
              moments — through practical care and compassionate presence.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 fb-reveal">
              <Link
                to="/donate"
                data-testid="hero-cta-donate"
                className="inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest transition-colors"
              >
                Donate Now <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#agencies"
                data-testid="hero-cta-agency"
                className="inline-flex items-center gap-3 border-2 border-white/70 hover:border-white text-white hover:bg-white hover:text-black px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all"
              >
                Partner Your Agency
              </a>
            </div>

            <div className="mt-10 fb-reveal">
              <LaunchCountdown variant="dark" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500 z-10" />
      </section>

      {/* MARQUEE STRIP */}
      <section aria-hidden className="border-y border-slate-200 bg-white overflow-hidden">
        <div className="flex fb-marquee whitespace-nowrap py-5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 pr-12 shrink-0">
              {["Meals", "Support", "Presence", "Crisis Response", "Family Care", "Agency Partnership", "Ohio Wide"].map((w) => (
                <span key={w} className="font-display text-2xl md:text-4xl font-black uppercase tracking-tight text-slate-900">
                  {w} <span className="text-blue-500 mx-6">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="py-24 md:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 fb-reveal">
            <div className="eyebrow text-blue-700 mb-4">About Feeding Blue</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
              Those who protect us deserve protection, too.
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              Feeding Blue exists to bridge practical care, family support, and trauma-informed
              partnerships for officers who are often underserved outside of crisis response
              systems. We meet families with dignity, humility, and excellence — because those who
              protect us deserve protection, too.
            </p>
            <div className="mt-10 relative overflow-hidden">
              <img
                src={IMG.behind_the_badge}
                alt="Behind the Badge — a real law enforcement family in a moment of joy"
                className="w-full aspect-[3/2] object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 fb-reveal">
            {[
              {
                label: "Our Mission",
                body: "To feed, uplift, and support law enforcement officers and their families during their most vulnerable moments through practical care and compassionate presence.",
              },
              {
                label: "Our Vision",
                body: "To become a trusted and sustainable support organization for law enforcement families — known for responding with compassion, honor, and practical help during seasons of trauma, grief, injury, and hardship.",
              },
              {
                label: "Our Story",
                body: "Headquartered in Columbus, Ohio, Feeding Blue was created to support officers and their families during grief, trauma, injury, crisis, and emotional hardship — with real meals and real presence.",
              },
              {
                label: "Our Method",
                body: "We bridge officers, families, chaplains, clinicians, and community partners into one relational network of care — practical first, and additional support when welcomed.",
              },
            ].map((c) => (
              <div key={c.label} className="border border-slate-200 p-8 hover:border-black transition-colors group">
                <div className="eyebrow text-slate-500 group-hover:text-blue-700 transition-colors">{c.label}</div>
                <p className="mt-4 text-slate-800 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE DIFFERENCE (dark) */}
      <section
        data-testid="difference-section"
        className="relative py-28 md:py-40 bg-black text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG.flag_silhouettes})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl fb-reveal">
            <div className="eyebrow text-blue-500 mb-6">The Difference</div>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              We don't just deliver meals.<br />
              <span className="text-blue-500">We deliver presence.</span>
            </h2>
            <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed">
              What sets us apart is our family-first approach, our personalized support for entire
              households, and our commitment to responding when crisis strikes. We stand beside law
              enforcement with presence, compassion, and resources designed to meet real needs in
              real time.
            </p>
            <div className="mt-10 h-1 w-24 bg-blue-500" />
            <p className="mt-6 font-display text-2xl md:text-3xl font-bold text-white/90">
              We feed the body. We steady the family.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section id="what-we-do" data-testid="what-we-do-section" className="py-24 md:py-40 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 fb-reveal items-end">
            <div className="md:col-span-7">
              <div className="eyebrow text-blue-700 mb-4">What We Do</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                Practical services that bring comfort and stability.
              </h2>
              <p className="mt-6 max-w-xl text-slate-600 leading-relaxed">
                Feeding Blue delivers care that meets both immediate needs and long-term healing —
                meal by meal, check-in by check-in.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="relative overflow-hidden">
                <img
                  src={IMG.basket_handoff}
                  alt="A Feeding Blue volunteer delivering a care basket to law enforcement officers"
                  className="w-full aspect-[3/2] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {[
              { icon: Utensils, title: "Crisis Meal & Grocery Support", body: "Hot meals, groceries, restaurant cards, delivery coordination, and emergency family food relief when a family is walking through crisis.", span: "md:col-span-6" },
              { icon: HandHeart, title: "Family Care & Encouragement", body: "Care baskets, practical support, child- and family-centered encouragement, and milestone recognition for the whole household.", span: "md:col-span-6" },
              { icon: HeartPulse, title: "Grief & Critical Incident Support", body: "Support for injured officers, families facing hospitalization, line-of-duty incidents, funerals, and major family loss.", span: "md:col-span-4" },
              { icon: MessageCircle, title: "Chaplain & Peer Support", body: "Chaplain coordination, peer encouragement, and pastoral check-ins — offered only when welcomed by the officer or family.", span: "md:col-span-4" },
              { icon: Sparkles, title: "Wellness & Restoration", body: "Trauma-informed referral relationships, family restoration resources, and wellness partnerships for officers and spouses.", span: "md:col-span-4" },
              { icon: Trophy, title: "Community Honor & Appreciation Events", body: "Retreats, appreciation dinners, and events that celebrate and strengthen law enforcement families.", span: "md:col-span-12" },
            ].map((s) => (
              <div
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`${s.span} bg-white border border-slate-200 p-8 md:p-10 hover:border-black hover:-translate-y-1 transition-all duration-300 group fb-reveal`}
              >
                <s.icon className="w-10 h-10 text-blue-700 group-hover:text-black transition-colors" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" data-testid="how-it-works-section" className="py-24 md:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16 fb-reveal">
            <div className="eyebrow text-blue-700 mb-4">How It Works</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
              Simplicity. Compassion. Confidentiality.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-slate-200">
            {[
              { n: "01", icon: HeartPulse, title: "A Need Arises", body: "An officer or family enters a season of grief, trauma, illness, or crisis." },
              { n: "02", icon: PhoneIcon, title: "You Contact Us", body: "Requests can come from officers, chaplains, supervisors, or loved ones." },
              { n: "03", icon: Clock, title: "We Respond in 24–48 hrs", body: "Meals and resources are delivered promptly, with chaplain support available upon request." },
              { n: "04", icon: Repeat, title: "Continued Care", body: "We follow up with families for ongoing encouragement and support as needed." },
            ].map((step, i) => (
              <div
                key={step.n}
                data-testid={`step-${step.n}`}
                className={`p-8 md:p-10 border-b md:border-b-0 md:border-r border-slate-200 last:border-r-0 fb-reveal ${i === 3 ? "" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl font-black text-slate-200">{step.n}</span>
                  <step.icon className="w-7 h-7 text-blue-700" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR AGENCIES / COLUMBUS */}
      <section id="agencies" data-testid="agencies-section" className="py-24 md:py-40 bg-black text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 fb-reveal">
            <div className="eyebrow text-blue-500 mb-4">Columbus & Ohio Law Enforcement</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              A trusted, internal resource for your department.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-lg">
              Feeding Blue partners directly with agencies across Columbus and Ohio to strengthen
              wellness, morale, and family support. Referrals come from supervisors, command staff,
              peer officers, chaplains, and wellness teams — anyone who sees a family in need.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Utensils, t: "Crisis Meals & Rapid Response", b: "Hot meals, snacks, or groceries delivered during critical incidents or long investigations." },
                { icon: HandHeart, t: "Family Care & Encouragement", b: "Meal delivery, groceries, and optional chaplain & grief support for families." },
                { icon: ShieldCheck, t: "Wellness Integration", b: "Emotional support during difficult seasons and burnout." },
                { icon: Building2, t: "MOU Partnership", b: "A simple, non-financial Memorandum of Understanding recognizes us as an internal resource." },
              ].map((x) => (
                <div key={x.t} className="border border-slate-800 p-6 hover:border-blue-500 transition-colors">
                  <x.icon className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                  <div className="mt-3 font-display text-lg font-bold">{x.t}</div>
                  <p className="text-sm text-slate-400 mt-1 leading-relaxed">{x.b}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="mailto:partnerships@feedingblue.org?subject=Feeding Blue MOU Partnership Inquiry"
                data-testid="agency-cta-mou"
                className="inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Start MOU Process <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="mailto:support@feedingblue.org?subject=Feeding Blue Agency Support Request"
                data-testid="agency-cta-request"
                className="inline-flex items-center gap-3 border border-white/50 hover:bg-white hover:text-black text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all"
              >
                Request Support
              </a>
              <a
                href="mailto:partnerships@feedingblue.org"
                data-testid="agency-email-link"
                className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400 transition-colors link-brut"
              >
                <Mail className="w-4 h-4" strokeWidth={2} />
                partnerships@feedingblue.org
              </a>
            </div>
          </div>

          <div className="md:col-span-6 fb-reveal">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={IMG.columbus} alt="Columbus, Ohio skyline" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                <div>
                  <div className="eyebrow text-blue-500">Based in</div>
                  <div className="font-display text-3xl font-black">Columbus, Ohio</div>
                </div>
                <MapPin className="w-8 h-8 text-blue-500" strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              {[
                { k: "24–48h", v: "Response Time" },
                { k: "Confidential", v: "Every Request" },
                { k: "Non-Judgmental", v: "Every Family" },
              ].map((s) => (
                <div key={s.v} className="border border-slate-800 p-4">
                  <div className="font-display text-xl font-black text-blue-500">{s.k}</div>
                  <div className="text-xs text-slate-400 mt-1 uppercase tracking-widest">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE VISION / NATIONWIDE GOAL */}
      <section id="vision" data-testid="vision-section" className="py-24 md:py-32 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            {/* Left: narrative */}
            <div className="md:col-span-6 fb-reveal">
              <div className="inline-flex items-center gap-2 border border-blue-700 bg-blue-50 px-3 py-1.5 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600" />
                </span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-blue-800">
                  Launching January 2027
                </span>
              </div>

              <div className="eyebrow text-blue-700 mb-4">The Vision</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                A household name for law enforcement <span className="text-blue-700">in America.</span>
              </h2>
              <div className="mt-8 space-y-5 text-slate-700 leading-relaxed text-base md:text-lg max-w-xl">
                <p>
                  Feeding Blue is being built to become a nationwide organization — the trusted
                  name officers and families call on during the hardest seasons of the profession.
                </p>
                <p>
                  Our goal is <strong className="text-slate-900">at least one base in every state</strong> — a
                  connected network of volunteers, partners, and support ready to show up for law
                  enforcement families anywhere they serve.
                </p>
                <p className="text-slate-900 font-semibold">
                  We officially launch <span className="text-blue-700">January 2027</span>.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3">
                {[
                  { k: "50", v: "States" },
                  { k: "1+", v: "Base Per State" },
                  { k: "Jan '27", v: "Launch" },
                ].map((s) => (
                  <div key={s.v} className="border border-slate-200 p-4 hover:border-blue-700 transition-colors" data-testid={`vision-stat-${s.v.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <div className="font-display text-2xl font-black text-slate-900">{s.k}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">{s.v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#join-team"
                  data-testid="vision-cta-join"
                  className="inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
                >
                  Help Build a Base <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/donate"
                  data-testid="vision-cta-donate"
                  className="inline-flex items-center gap-3 border-2 border-black hover:bg-black hover:text-white text-black px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all"
                >
                  Fuel the Launch
                </Link>
              </div>
            </div>

            {/* Right: branded launch banner */}
            <div className="md:col-span-6 fb-reveal">
              <div className="relative overflow-hidden shadow-2xl shadow-black/20">
                <img
                  src={IMG.launch_banner}
                  alt="Feeding Blue is launching January 2027 — Faith. Service. Community."
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 text-xs text-slate-500 text-center uppercase tracking-widest">
                Together, we can make a difference · #FeedingBlue
              </p>
            </div>
          </div>

          <div className="mt-16 pt-12 border-t border-slate-200 fb-reveal">
            <div className="max-w-3xl mb-8">
              <div className="eyebrow text-blue-700 mb-3">Our Nationwide Network</div>
              <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
                One base in every state.
              </h3>
            </div>
            <USBasesMap />
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" data-testid="events-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 fb-reveal">
            <div>
              <div className="eyebrow text-blue-700 mb-4">Events</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900 max-w-3xl">
                Rally the community. Fund the mission.
              </h2>
            </div>
            <p className="max-w-md text-slate-600 leading-relaxed">
              From softball diamonds to gala ballrooms — our events raise support, build community,
              and put a public face on Feeding Blue's mission.
            </p>
          </div>

          {/* Featured event — Battle of the Badges */}
          <div
            data-testid="event-badges"
            className="relative overflow-hidden bg-black text-white p-10 md:p-16 mb-6 fb-reveal group"
          >
            <div
              className="absolute inset-0 bg-cover bg-center opacity-25 group-hover:opacity-30 transition-opacity"
              style={{ backgroundImage: `url(${IMG.badge})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <div className="eyebrow text-blue-400">Signature Fundraiser</div>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">
                  Battle of the Badges<br />
                  <span className="text-blue-400">Softball Tournament</span>
                </h3>
                <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl">
                  A law-enforcement vs. law-enforcement softball tournament that brings agencies
                  together for a fun, competitive fundraiser. A family-friendly community event
                  with spectators, vendors, kids activities, and department participation from
                  around Ohio — with proceeds supporting officers and families in crisis.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {["Agency vs. Agency", "Kids Activities", "Vendors", "Public Safety Displays"].map((tag) => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-widest border border-white/30 px-3 py-1.5">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <div className="w-24 h-24 md:w-32 md:h-32 flex items-center justify-center border-2 border-blue-400">
                  <Trophy className="w-12 h-12 md:w-16 md:h-16 text-blue-400" strokeWidth={1.5} />
                </div>
              </div>
            </div>
          </div>

          {/* Grid of remaining events */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {[
              {
                tid: "event-gala",
                eyebrow: "Formal Fundraiser",
                title: "Blue Tie Gala",
                body: "A formal evening designed to launch broader donor support and community credibility — where supporters, community leaders, and law enforcement partners hear the mission, give financially, and connect to the vision.",
                icon: Sparkles,
                span: "md:col-span-6",
              },
              {
                tid: "event-retreat",
                eyebrow: "Officers Only",
                title: "Blue Brotherhood Retreat",
                body: "A men's retreat built specifically for law enforcement officers — space to rest, connect, and receive practical and emotional encouragement, with optional peer and chaplain support available.",
                icon: Users,
                span: "md:col-span-6",
              },
              {
                tid: "event-harvest",
                eyebrow: "Fall Community Event",
                title: "Blue Harvest Festival",
                body: "A fall fundraiser and family-friendly community event with food, vendors, activities, and law-enforcement engagement — specialty unit displays, public safety participation, and seasonal celebration.",
                icon: Leaf,
                span: "md:col-span-4",
              },
              {
                tid: "event-conference",
                eyebrow: "Two-Day Gathering",
                title: "Blue Bridge Conference",
                body: "A relationship-building conference bridging community leaders and law enforcement — conversations around trauma support, resilience, wellness, and long-term partnership.",
                icon: Calendar,
                span: "md:col-span-8",
              },
            ].map((e) => (
              <div
                key={e.tid}
                data-testid={e.tid}
                className={`${e.span} border border-slate-200 p-8 md:p-10 hover:border-black hover:-translate-y-1 transition-all duration-300 group fb-reveal`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <div className="eyebrow text-blue-700">{e.eyebrow}</div>
                    <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                      {e.title}
                    </h3>
                    <p className="mt-3 text-slate-600 leading-relaxed">{e.body}</p>
                  </div>
                  <div className="w-14 h-14 flex items-center justify-center border border-slate-200 group-hover:border-blue-700 group-hover:bg-blue-700 transition-colors shrink-0">
                    <e.icon className="w-6 h-6 text-blue-700 group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4 fb-reveal">
            <a
              href="mailto:events@feedingblue.org"
              data-testid="events-sponsor-cta"
              className="inline-flex items-center gap-3 bg-black hover:bg-neutral-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
            >
              Sponsor an Event <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="mailto:events@feedingblue.org?subject=Feeding Blue Event Updates"
              data-testid="events-notify-cta"
              className="inline-flex items-center gap-3 border-2 border-black hover:bg-black hover:text-white text-black px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all"
            >
              Get Event Updates
            </a>
            <a
              href="mailto:events@feedingblue.org"
              data-testid="events-email-link"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-black transition-colors link-brut"
            >
              <Mail className="w-4 h-4" strokeWidth={2} />
              events@feedingblue.org
            </a>
          </div>
        </div>
      </section>

      {/* GET INVOLVED */}
      <section id="get-involved" data-testid="get-involved-section" className="py-24 md:py-32 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10 fb-reveal">
            <div className="eyebrow text-blue-700 mb-4">Get Involved</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
              How you can stand with us.
            </h2>
          </div>

          {/* Branded 3-panel banner */}
          <div className="relative overflow-hidden mb-12 fb-reveal">
            <img
              src={IMG.bring_hope_meals_presence}
              alt="Bring Hope, Bring Meals, Bring Presence — three ways to support law enforcement families through Feeding Blue"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 fb-reveal">
            {[
              { icon: Users, t: "Volunteer", b: "Join our trained volunteers who deliver meals, check in on families, and serve with dignity and compassion.", cta: "Sign up to serve", tid: "volunteer" },
              { icon: HandHeart, t: "Donate", b: "Every gift helps feed a family, comfort a grieving officer, or support a department in crisis.", cta: "Give today", tid: "donate", link: "/donate" },
              { icon: Handshake, t: "Partner With Us", b: "Businesses, churches, and organizations can become official Feeding Blue partners through sponsorships or programs.", cta: "Become a partner", tid: "partner" },
            ].map((x) => (
              <div key={x.t} className="group bg-white border border-slate-200 p-8 md:p-10 hover:border-blue-700 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-black text-white group-hover:bg-blue-700 transition-colors">
                  <x.icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-slate-900">{x.t}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{x.b}</p>
                {x.link ? (
                  <Link
                    to={x.link}
                    data-testid={`get-involved-${x.tid}`}
                    className="inline-flex items-center gap-2 mt-6 text-xs font-bold uppercase tracking-widest text-blue-700 hover:text-black transition-colors"
                  >
                    {x.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <a
                    href="#contact"
                    data-testid={`get-involved-${x.tid}`}
                    className="inline-flex items-center gap-2 mt-6 text-xs font-bold uppercase tracking-widest text-blue-700 hover:text-black transition-colors"
                  >
                    {x.cta} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS COMING SOON */}
      <section id="testimonials" data-testid="testimonials-section" className="relative py-24 md:py-32 bg-slate-50 border-y border-slate-200 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG.warm_dinner})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-transparent to-slate-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center fb-reveal">
          <div className="eyebrow text-blue-700 mb-4">Testimonials</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
            Coming soon.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-slate-600 leading-relaxed">
            Stories of real officers and families whose lives have been touched by Feeding Blue.
            If you'd like to share yours, we'd be honored to hear it.
          </p>
          <a
            href="#contact"
            data-testid="testimonial-cta"
            className="mt-8 inline-flex items-center gap-3 border-2 border-black hover:bg-black hover:text-white text-slate-900 px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all"
          >
            Share Your Story <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* OUR ORIGIN STORY */}
      <section id="story" data-testid="story-section" className="py-24 md:py-40 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
            {/* Left column — heading + pullquote */}
            <div className="md:col-span-5 fb-reveal">
              <div className="eyebrow text-blue-700 mb-4">Our Origin Story</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                How Feeding Blue<br />
                <span className="text-blue-700">started.</span>
              </h2>

              <div className="mt-10 relative overflow-hidden">
                <img
                  src={IMG.volunteers_packing}
                  alt="Feeding Blue volunteers packing care packages for law enforcement families"
                  className="w-full aspect-[3/2] object-cover"
                  loading="lazy"
                />
              </div>

              <div className="mt-8 border-l-2 border-blue-700 pl-6 py-2">
                <p className="font-display text-xl md:text-2xl italic text-slate-800 leading-snug">
                  &ldquo;Officers simply weren&rsquo;t used to people showing up for them like
                  that. That moment stayed with her.&rdquo;
                </p>
                <p className="mt-4 eyebrow text-slate-500">The Origin of Feeding Blue</p>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-3">
                {[
                  { k: "2023", v: "The Seed" },
                  { k: "Milwaukee", v: "Where It Began" },
                  { k: "Columbus", v: "Where It Grew" },
                  { k: "Ohio", v: "Where It Serves" },
                ].map((s) => (
                  <div key={s.v} className="border border-slate-200 p-4">
                    <div className="font-display text-lg font-black text-slate-900">{s.k}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — narrative */}
            <div className="md:col-span-7 fb-reveal">
              <div className="space-y-6 text-slate-700 leading-relaxed text-base md:text-lg">
                <p>
                  The seed for Feeding Blue was planted back in <strong className="text-slate-900">2023</strong>,
                  when our founder <strong className="text-slate-900">O&rsquo;Shea Johnson</strong> was still
                  living in Wisconsin.
                </p>
                <p>
                  In Milwaukee — one of Wisconsin&rsquo;s major cities — a law enforcement officer
                  was killed in the line of duty. It was a significant moment for the city; the
                  community hadn&rsquo;t lost an officer on duty in years. Wisconsin has a strong
                  community culture. When something happens, people rally around each other and
                  support one another.
                </p>
                <p>
                  O&rsquo;Shea, her mother, and a close friend decided to put together
                  encouragement baskets with cards and small gifts. They drove to several police
                  districts in Milwaukee and personally delivered them to the officers on shift —
                  telling them they were supported, that the family was sorry for their loss, and
                  that they were standing with them.
                </p>
                <p>
                  The reactions they received were unforgettable — officers simply weren&rsquo;t
                  used to people showing up for them in that way. That moment stayed with her.
                </p>
                <p>
                  Later, when O&rsquo;Shea moved to Columbus, the experience kept coming back. She
                  kept thinking about how much that simple act meant to those officers, and
                  eventually found herself saying, <em>&ldquo;I want to do something like that
                  again.&rdquo;</em>
                </p>
                <p>
                  Over time, that idea began to grow into something more structured — a vision for
                  something that wouldn&rsquo;t just be occasional acts of kindness, but a
                  consistent and organized way to support law enforcement families.
                </p>
                <p>
                  Before moving forward, O&rsquo;Shea talked with trusted contacts — including a
                  sergeant — because she honestly didn&rsquo;t know if something like this would
                  even be accepted. As a civilian without any direct connection to law enforcement,
                  she wanted the perspective of those inside the profession.
                </p>
                <p className="font-display text-2xl md:text-3xl font-bold text-slate-900 pt-2">
                  But here we are.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY LAW ENFORCEMENT MATTERS */}
      <section
        id="why-matters"
        data-testid="why-matters-section"
        className="relative py-28 md:py-40 bg-black text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${IMG.badge})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-7 fb-reveal">
              <div className="eyebrow text-blue-400 mb-6">The Human Behind the Badge</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Why law enforcement<br />
                <span className="text-blue-400">matters to us.</span>
              </h2>

              <div className="mt-10 space-y-6 text-slate-300 leading-relaxed text-base md:text-lg max-w-2xl">
                <p>
                  One thing I&rsquo;ve always noticed is that many people tend to see police
                  officers as the &ldquo;bad guys,&rdquo; while firefighters are often seen as the
                  heroes. But what people forget is that officers are human beings first.
                </p>
                <p>
                  Behind the badge are real people with real emotions, real families, and real
                  pressures that most people will never fully understand.
                </p>
                <p className="text-white font-semibold text-xl md:text-2xl font-display leading-snug">
                  Feeding Blue exists to care for that human side.
                </p>
                <p>
                  Our goal is to support officers and their families during heavy seasons — grief,
                  trauma, injury, or simply the emotional weight that can come with the job.
                </p>
                <p>
                  Feeding Blue also offers optional chaplain and additional support when a family
                  welcomes it. Many officers carry things from work that spill into home life, and
                  sometimes home struggles spill back into work. Brokenness can move in both
                  directions.
                </p>
                <p>
                  The goal is to create space where officers and their families can receive
                  encouragement, practical care, and — if they choose it — additional support to
                  help them heal.
                </p>
              </div>
            </div>

            {/* Right column — pullquote card */}
            <div className="md:col-span-5 flex items-start fb-reveal">
              <div className="w-full border border-slate-800 bg-white/[0.02] backdrop-blur-sm p-8 md:p-10">
                <div className="text-blue-400 text-6xl leading-none font-display font-black">“</div>
                <p className="mt-2 font-display text-xl md:text-2xl text-white leading-snug">
                  Officers are human beings first. Behind the badge are real people with real
                  emotions, real families, and real pressures.
                </p>
                <div className="mt-8 h-px bg-slate-700" />
                <div className="mt-6 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="font-display text-3xl font-black text-blue-400">Grief</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Trauma • Injury</div>
                  </div>
                  <div>
                    <div className="font-display text-3xl font-black text-blue-400">Weight</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Of the Job</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR VALUES */}
      <section id="values" data-testid="values-section" className="py-24 md:py-40 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fb-reveal max-w-3xl mx-auto">
            <div className="eyebrow text-blue-700 mb-4">Our Values</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
              How we show up — every family, every time.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-lg">
              Feeding Blue operates by a set of principles that shape every conversation, every
              delivery, and every follow-up.
            </p>
          </div>

          <div className="mt-16 max-w-4xl mx-auto fb-reveal">
            <Accordion type="single" collapsible className="w-full">
              {[
                { id: "compassion", icon: HandHeart, t: "Compassion First", b: "We meet families where they are. Every meal, check-in, and follow-up is delivered with warmth, patience, and dignity — with no expectation in return." },
                { id: "confidentiality", icon: ShieldCheck, t: "Confidentiality", b: "Requests, family details, and incident context stay between Feeding Blue and the requesting party. We protect the family's privacy the same way officers protect the community." },
                { id: "excellence", icon: Sparkles, t: "Excellence in Care", b: "Officers and families deserve professional, well-executed support — clean deliveries, on-time response, clear communication, and thoughtful follow-through." },
                { id: "trauma", icon: HeartPulse, t: "Trauma-Informed Approach", b: "We train volunteers to recognize the emotional weight officers and families carry. Every interaction is calm, non-invasive, and centered on the family's stated needs." },
                { id: "optional-support", icon: BookOpen, t: "Optional Chaplain Support", b: "Chaplain coordination and additional encouragement are available when a family welcomes them — never assumed, and never a condition of receiving care." },
                { id: "action", icon: Handshake, t: "Action Over Words", b: "Support matters only when it shows up on the doorstep. Our commitment is action: real meals, real presence, and real follow-up — because no officer or family should walk through crisis alone." },
              ].map((b) => (
                <AccordionItem key={b.id} value={b.id} className="border-b border-slate-200" data-testid={`value-${b.id}`}>
                  <AccordionTrigger className="py-6 hover:no-underline group">
                    <div className="flex items-center gap-4 text-left">
                      <b.icon className="w-6 h-6 text-blue-700 shrink-0" strokeWidth={1.5} />
                      <span className="font-display text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
                        {b.t}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pl-10 text-slate-600 text-base leading-relaxed">
                    {b.b}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* LEADERSHIP — FOUNDER */}
      <section id="leadership" data-testid="leadership-section" className="py-24 md:py-40 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
            {/* Portrait */}
            <div className="md:col-span-5 fb-reveal">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-blue-700 -z-0" aria-hidden />
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-black -z-0" aria-hidden />
                <div className="relative overflow-hidden bg-slate-100">
                  <img
                    src="/assets/founder.jpg"
                    alt="O'Shea Johnson, Founder and National President of Feeding Blue"
                    className="w-full h-auto object-cover"
                    data-testid="founder-portrait"
                  />
                </div>
              </div>

              <div className="mt-8">
                <div className="eyebrow text-blue-700">Founder & National President</div>
                <h3 className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-none">
                  O'Shea Johnson
                </h3>
                <a
                  href="mailto:President@feedingblue.org"
                  data-testid="founder-email"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700 transition-colors link-brut"
                >
                  <Mail className="w-4 h-4" strokeWidth={2} />
                  President@feedingblue.org
                </a>
              </div>
            </div>

            {/* Bio */}
            <div className="md:col-span-7 fb-reveal">
              <div className="eyebrow text-blue-700 mb-4">Leadership</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                Built with structure, integrity, and long-term vision.
              </h2>
              <div className="mt-8 space-y-5 text-slate-700 leading-relaxed text-base md:text-lg">
                <p>
                  O'Shea Johnson is the Founder and President of Feeding Blue, a nonprofit created
                  to support law enforcement officers and their families during seasons of grief,
                  trauma, injury, and hardship.
                </p>
                <p>
                  Feeding Blue was founded from a simple but weighty conviction: behind every badge
                  is a human being, and behind many officers is a family carrying the weight of the
                  profession alongside them. What began as a burden to stand with law enforcement
                  families grew into a larger vision — one centered on practical support,
                  compassion, and consistent care during some of life's heaviest moments.
                </p>
                <p>
                  As Founder and President, O'Shea leads Feeding Blue with a strong emphasis on
                  structure, integrity, and long-term vision. She is a builder by nature — someone
                  who naturally thinks in terms of systems, leadership, and sustainability. That
                  mindset has shaped the foundation of Feeding Blue from the beginning. Her goal
                  has never been to create a temporary outreach effort, but to build an
                  organization that is trustworthy, organized, and positioned to serve law
                  enforcement families well for years to come.
                </p>
                <p>
                  Although O'Shea does not come from a law enforcement background, Feeding Blue was
                  built out of a deep respect for those who serve and a recognition that officers
                  and their families often carry burdens the public never fully sees. Her heart
                  behind the organization is to make sure those families know they are not
                  forgotten — especially in moments of crisis, loss, recovery, and emotional
                  strain.
                </p>
                <p>
                  Under her leadership, Feeding Blue is being built to provide both practical and
                  compassionate support through meals, groceries, gift cards, encouragement, and
                  optional additional care. At its core, the mission is to care for the people
                  behind the badge and to serve law enforcement families with dignity, consistency,
                  and genuine compassion.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3">
                {[
                  { k: "Founder", v: "& National President" },
                  { k: "Builder", v: "By Nature" },
                  { k: "Columbus", v: "Ohio" },
                ].map((s) => (
                  <div key={s.v} className="border border-slate-200 p-4 hover:border-blue-700 transition-colors">
                    <div className="font-display text-lg font-black text-slate-900">{s.k}</div>
                    <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-widest">{s.v}</div>
                  </div>
                ))}
              </div>

              <p className="mt-10 text-xs uppercase tracking-widest text-slate-400">
                More leadership bios coming as roles are filled.
              </p>
            </div>
          </div>

          {/* Paul Highfill — National Director of Clinical Care */}
          <div className="mt-24 md:mt-32 pt-16 md:pt-20 border-t border-slate-200 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start" data-testid="leader-paul-highfill">
            {/* Bio on the left, portrait on the right (flipped from O'Shea) */}
            <div className="md:col-span-7 md:order-1 order-2 fb-reveal">
              <div className="eyebrow text-blue-700 mb-4">National Leadership</div>
              <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                Caring for the whole person.
              </h3>
              <div className="mt-8 space-y-5 text-slate-700 leading-relaxed text-base md:text-lg">
                <p>
                  Paul Highfill is a Licensed Marriage and Family Therapist with more than
                  <strong className="text-slate-900"> 25 years of experience</strong> in counseling,
                  foster care, family services, ministry, leadership, and trauma-informed care.
                  Throughout his career, he has walked alongside individuals and families facing
                  crisis, trauma, life transitions, and seasons of uncertainty — helping them find
                  stability, healing, and hope.
                </p>
                <p>
                  Paul Highfill, LMFT serves as Feeding Blue&rsquo;s
                  <strong className="text-slate-900"> National Director of Clinical Care</strong>
                  {" "}and
                  <strong className="text-slate-900"> Regional President, Tennessee</strong>.
                </p>
                <p>
                  In his national role, Paul provides professional leadership and guidance in the
                  development of Feeding Blue&rsquo;s clinical care initiatives, helping shape the
                  organization&rsquo;s approach to supporting law enforcement personnel and their
                  families through grief, trauma, emotional wellness, and other critical needs.
                </p>
                <p>
                  As Regional President, Tennessee, he also leads the development and growth of
                  Feeding Blue&rsquo;s Tennessee presence — helping establish local partnerships,
                  build relationships with law enforcement agencies and communities, and ensure
                  regional efforts remain aligned with Feeding Blue&rsquo;s national mission and
                  vision.
                </p>
                <p>
                  Paul&rsquo;s approach is rooted in caring for the whole person. He combines
                  therapeutic expertise, biblical truth, spiritual formation, and practical
                  guidance to foster emotional, relational, and spiritual well-being. His vision is
                  to help build a culture where officers and their families know they are valued,
                  supported, and never have to face life&rsquo;s challenges alone.
                </p>
              </div>

              {/* Quote pullout */}
              <div className="mt-10 border-l-2 border-blue-700 pl-6 py-2">
                <p className="font-display text-xl md:text-2xl italic text-slate-800 leading-snug">
                  &ldquo;I&rsquo;m passionate about this work because officers and their families
                  carry so much that most people never see. I want to help create support that
                  feels safe, trusted, and real — a space where asking for help feels like
                  strength, not failure. Carrying the weight of protecting those who need it most
                  deserves to be an effort shared by the community they serve.&rdquo;
                </p>
                <p className="mt-4 eyebrow text-slate-500">Paul Highfill · National Director of Clinical Care</p>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3">
                {[
                  { k: "National", v: "Clinical Care" },
                  { k: "Tennessee", v: "Regional President" },
                  { k: "LMFT", v: "Licensed" },
                ].map((s) => (
                  <div key={s.v} className="border border-slate-200 p-4 hover:border-blue-700 transition-colors">
                    <div className="font-display text-base font-black text-slate-900">{s.k}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Portrait on the right */}
            <div className="md:col-span-5 md:order-2 order-1 fb-reveal">
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-blue-700 -z-0" aria-hidden />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-black -z-0" aria-hidden />
                <div className="relative overflow-hidden bg-slate-100">
                  <img
                    src="/assets/paul-highfill.jpg"
                    alt="Paul Highfill, LMFT — National Director of Clinical Care and Regional President, Feeding Blue Tennessee"
                    className="w-full h-auto object-cover"
                    data-testid="paul-portrait"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="mt-8">
                <div className="eyebrow text-blue-700">National Director · Regional President</div>
                <h4 className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-none">
                  Paul Highfill, LMFT
                </h4>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  National Director of Clinical Care<br />
                  <span className="text-slate-500">Regional President, Feeding Blue Tennessee</span>
                </p>
                <a
                  href="mailto:Phighfill@feedingblue.org"
                  data-testid="paul-email"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700 transition-colors link-brut"
                >
                  <Mail className="w-4 h-4" strokeWidth={2} />
                  Phighfill@feedingblue.org
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOIN THE FOUNDING TEAM */}
      <section id="join-team" data-testid="join-team-section" className="py-24 md:py-32 bg-black text-white relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${IMG.badge})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14 fb-reveal">
            <div className="eyebrow text-blue-400 mb-4">Now Hiring — Volunteer</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Join the Founding Leadership Team.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-lg">
              Feeding Blue is assembling a founding team of directors, officers, and board members
              to help build the organization from the ground up. All positions are volunteer roles
              until further expansion.
            </p>
          </div>

          {/* Directors */}
          <div className="mb-4 fb-reveal">
            <div className="eyebrow text-slate-400 mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-slate-700" />
              Open Director Positions
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14">
            {[
              {
                tid: "role-women-family",
                icon: HandHeart,
                title: "Director of Women & Family Restoration Services",
                body: "Focuses on the unique needs of women in law enforcement, spouses, and police families navigating hardship, stress, grief, or major life transitions. Helps shape support initiatives that bring encouragement, restoration, and practical care to the family side of the badge.",
              },
              {
                tid: "role-volunteer",
                icon: Users,
                title: "Director of Volunteer Services",
                body: "Builds and leads the volunteer structure of Feeding Blue. Oversees volunteer recruitment, coordination, training, and engagement for meal delivery, outreach efforts, events, and other service opportunities that directly support officers and their families.",
              },
              {
                tid: "role-partnerships",
                icon: Handshake,
                title: "Director of Community Partnerships",
                body: "Builds relationships with churches, businesses, nonprofits, and community leaders who can strengthen Feeding Blue's mission. Creates meaningful partnerships that expand resources, collaboration, and support for the officers and families we serve.",
              },
              {
                tid: "role-fundraising",
                icon: LineChart,
                title: "Director of Fundraising & Development",
                body: "Supports the financial growth and long-term sustainability of Feeding Blue. Focuses on fundraising strategy, donor relations, sponsorship outreach, campaigns, and development opportunities that fund the mission and expand impact.",
              },
            ].map((r) => (
              <div
                key={r.tid}
                data-testid={r.tid}
                className="border border-slate-800 p-8 hover:border-blue-500 hover:bg-white/[0.02] transition-all group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center border border-slate-700 group-hover:border-blue-500 group-hover:bg-blue-500 transition-colors shrink-0">
                    <r.icon className="w-5 h-5 text-blue-400 group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="eyebrow text-blue-400 text-[10px]">Director</div>
                    <h3 className="mt-1 font-display text-xl md:text-2xl font-bold tracking-tight text-white">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{r.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Board / Officers */}
          <div className="mb-4 fb-reveal">
            <div className="eyebrow text-slate-400 mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-slate-700" />
              Open Board / Officer Positions
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                tid: "role-treasurer",
                icon: Coins,
                title: "Treasurer",
                body: "Serves as the financial steward of Feeding Blue and helps ensure the organization operates with integrity, accountability, and strong financial oversight. Oversees budgeting, financial records, reporting, and general financial organization — protecting the long-term health and stability of the organization.",
              },
              {
                tid: "role-vp",
                icon: Briefcase,
                title: "Second Vice President — Head of Ministry Operations",
                body: "Helps oversee the care and chaplain-support side of Feeding Blue. Supports encouragement initiatives, chaplain collaboration, and family-support outreach — helping ensure those elements are carried out with wisdom, care, and alignment with Feeding Blue's values.",
              },
            ].map((r) => (
              <div
                key={r.tid}
                data-testid={r.tid}
                className="border border-slate-800 p-8 hover:border-blue-500 hover:bg-white/[0.02] transition-all group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center border border-slate-700 group-hover:border-blue-500 group-hover:bg-blue-500 transition-colors shrink-0">
                    <r.icon className="w-5 h-5 text-blue-400 group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="eyebrow text-blue-400 text-[10px]">Officer</div>
                    <h3 className="mt-1 font-display text-xl md:text-2xl font-bold tracking-tight text-white">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{r.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact strip */}
          <div className="mt-14 border-t border-slate-800 pt-10 flex flex-col md:flex-row md:items-center justify-between gap-6 fb-reveal">
            <div>
              <div className="eyebrow text-blue-400 mb-2">For more information</div>
              <div className="font-display text-2xl md:text-3xl font-black tracking-tight">
                Contact <span className="text-blue-400">President@feedingblue.org</span>
              </div>
              <p className="mt-3 text-sm text-slate-400 max-w-xl">
                Send a brief note about the role you're interested in, your background, and why
                you'd like to help build Feeding Blue.
              </p>
            </div>
            <a
              href="mailto:President@feedingblue.org?subject=Feeding Blue Leadership Interest"
              data-testid="join-team-cta"
              className="inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors self-start"
            >
              Apply / Inquire <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT + DONATE STRIP */}
      <section id="contact" data-testid="contact-section" className="py-24 md:py-40 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-7 fb-reveal">
            <div className="eyebrow text-blue-700 mb-4">Contact Us</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
              We're here to serve.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">
              Whether you're an officer, a family member, a department representative, or a
              supporter — reach out and we'll respond promptly.
            </p>

            {/* Email directory */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="email-directory">
              {[
                { label: "General", email: "info@feedingblue.org", tid: "email-info" },
                { label: "Family Support", email: "support@feedingblue.org", tid: "email-support" },
                { label: "Events & Sponsorship", email: "events@feedingblue.org", tid: "email-events" },
                { label: "Partnerships & MOUs", email: "partnerships@feedingblue.org", tid: "email-partnerships" },
              ].map((e) => (
                <a
                  key={e.email}
                  href={`mailto:${e.email}`}
                  data-testid={e.tid}
                  className="group border border-slate-200 hover:border-black bg-white p-4 flex items-center justify-between gap-3 transition-colors"
                >
                  <div>
                    <div className="eyebrow text-blue-700 text-[10px]">{e.label}</div>
                    <div className="mt-1 font-semibold text-slate-900 text-sm break-all">{e.email}</div>
                  </div>
                  <Mail className="w-4 h-4 text-slate-400 group-hover:text-black shrink-0" strokeWidth={2} />
                </a>
              ))}
            </div>

            <div className="mt-10">
              <ContactForm />
            </div>
          </div>

          <div className="md:col-span-5 fb-reveal">
            <div className="bg-black text-white p-10 md:p-12 h-full flex flex-col relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-20 bg-cover bg-center"
                style={{ backgroundImage: `url(${IMG.neighborly_help})` }}
                aria-hidden
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />
              <div className="relative z-10 flex flex-col h-full">
                <div className="eyebrow text-blue-500">Support the mission</div>
                <h3 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight">
                  Your gift feeds a family, comforts a grieving officer, supports a department in crisis.
                </h3>
                <p className="mt-4 text-slate-400 leading-relaxed">
                  Every dollar becomes a meal, a check-in, a knock on the door when a family needs
                  someone. Partner with Feeding Blue today.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-3">
                  {[50, 100, 250, 500].map((n) => (
                    <Link
                      key={n}
                      to={`/donate?amount=${n}`}
                      data-testid={`donate-shortcut-${n}`}
                      className="border border-slate-700 hover:border-blue-500 hover:bg-blue-500 hover:text-white text-center px-4 py-4 font-display font-black text-xl transition-colors"
                    >
                      ${n}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/donate"
                  data-testid="contact-donate-cta"
                  className="mt-8 inline-flex items-center justify-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
                >
                  Donate Now <ArrowRight className="w-4 h-4" />
                </Link>

                <div className="mt-auto pt-10 space-y-3 text-sm text-slate-300">
                  <a href="mailto:info@feedingblue.org" className="block hover:text-blue-400 transition-colors" data-testid="contact-side-info">info@feedingblue.org</a>
                  <a href="mailto:support@feedingblue.org" className="block hover:text-blue-400 transition-colors" data-testid="contact-side-support">support@feedingblue.org</a>
                  <div className="pt-2 text-slate-400">(614) XXX-XXXX</div>
                  <div className="text-slate-400">Columbus, Ohio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
